import { fetchDocument as fetchDocument$1, isSavedToPod, hasRef, createDocument, createDocumentInContainer } from 'tripledoc';
import SolidAuth from 'solid-auth-client';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

/**
 * Construct a [[VirtualContainer]].
 *
 * This function allows you to programmatically construct a [[VirtualContainer]] by chaining
 * together a number of functions.
 */
function describeContainer() {
    return {
        experimental_isFoundAt: isFoundAt,
        isFoundOn: isFoundOn,
        experimental_isContainedIn: isContainedIn
    };
}
/**
 * Describe a Container for which you know the IRI.
 *
 * @ignore Experimental API.
 * @param reference IRI of the desired Container.
 */
function isFoundAt(reference) {
    return {
        internal_descriptor: {
            type: "IsFoundAt",
            reference: reference
        }
    };
}
/**
 * Describe a Container that is referred to by a given Subject.
 *
 * @param subject [[VirtualSubject]] describing the Subject that points to this Container.
 * @param predicate Predicate that is used on `subject` to point to this Container.
 */
function isFoundOn(subject, predicate) {
    return {
        internal_descriptor: {
            type: "IsFoundOn",
            subject: subject,
            predicate: predicate
        }
    };
}
/**
 * Describe a Container that should be contained in another Container.
 *
 * If a Container by the given name does not exist yet in the given Container, it will be created on
 * the fly.
 *
 * @ignore Still an experimental API.
 * @param container [[VirtualContainer]] that contains this Container.
 * @param name The name this Container should have inside the given Container.
 */
function isContainedIn(container, name) {
    return generateContainedVirtualContainer({
        type: "IsContainedIn",
        container: container,
        name: name,
        acl: {}
    });
}
/**
 * @ignore Internal API.
 */
function generateContainedVirtualContainer(descriptor) {
    return {
        internal_descriptor: descriptor,
        experimental_isReadableByEveryone: generateSetPublicAcl(descriptor, "read"),
        experimental_isAppendableByEveryone: generateSetPublicAcl(descriptor, "append"),
        experimental_isWritableByEveryone: generateSetPublicAcl(descriptor, "write"),
        experimental_isControllableByEveryone: generateSetPublicAcl(descriptor, "control"),
        experimental_isReadableByAgent: generateSetAgentAcl(descriptor, "read"),
        experimental_isAppendableByAgent: generateSetAgentAcl(descriptor, "append"),
        experimental_isWritableByAgent: generateSetAgentAcl(descriptor, "write"),
        experimental_isControllableByAgent: generateSetAgentAcl(descriptor, "control"),
        experimental_isReadableByOrigin: generateSetOriginAcl(descriptor, "read"),
        experimental_isAppendableByOrigin: generateSetOriginAcl(descriptor, "append"),
        experimental_isWritableByOrigin: generateSetOriginAcl(descriptor, "write"),
        experimental_isControllableByOrigin: generateSetOriginAcl(descriptor, "control")
    };
}
function generateSetOriginAcl(descriptor, accessMode) {
    return function (origin, agent) {
        var _a, _b, _c, _d;
        var acl = (_a = descriptor.acl, (_a !== null && _a !== void 0 ? _a : {}));
        acl.origins = (_b = acl.origins, (_b !== null && _b !== void 0 ? _b : {}));
        acl.origins[origin] = (_c = acl.origins[origin], (_c !== null && _c !== void 0 ? _c : {}));
        acl.origins[origin][agent] = (_d = acl.origins[origin][agent], (_d !== null && _d !== void 0 ? _d : {}));
        acl.origins[origin][agent][accessMode] = true;
        var newDescriptor = __assign(__assign({}, descriptor), { acl: acl });
        return generateContainedVirtualContainer(newDescriptor);
    };
}
function generateSetAgentAcl(descriptor, accessMode) {
    return function (agent) {
        var _a, _b, _c;
        var acl = (_a = descriptor.acl, (_a !== null && _a !== void 0 ? _a : {}));
        acl.agents = (_b = acl.agents, (_b !== null && _b !== void 0 ? _b : {}));
        acl.agents[agent] = (_c = acl.agents[agent], (_c !== null && _c !== void 0 ? _c : {}));
        acl.agents[agent][accessMode] = true;
        var newDescriptor = __assign(__assign({}, descriptor), { acl: acl });
        return generateContainedVirtualContainer(newDescriptor);
    };
}
function generateSetPublicAcl(descriptor, accessMode) {
    return function () {
        var _a, _b;
        var acl = (_a = descriptor.acl, (_a !== null && _a !== void 0 ? _a : {}));
        acl.public = (_b = acl.public, (_b !== null && _b !== void 0 ? _b : {}));
        acl.public[accessMode] = true;
        var newDescriptor = __assign(__assign({}, descriptor), { acl: acl });
        return generateContainedVirtualContainer(newDescriptor);
    };
}

/**
 * Construct a [[VirtualDocument]].
 *
 * This function allows you to programmatically construct a [[VirtualDocument]] by chaining
 * together a number of functions.
 */
function describeDocument() {
    return {
        isFoundAt: isFoundAt$1,
        isFoundOn: isFoundOn$1,
        isEnsuredOn: isEnsuredOn,
        experimental_isAclFor: isAclFor
    };
}
/**
 * Describe a Document for which you know the IRI.
 *
 * @param reference IRI of the desired Document.
 */
function isFoundAt$1(reference) {
    return {
        internal_descriptor: {
            type: "IsFoundAt",
            reference: reference
        }
    };
}
/**
 * Describe a Document that acts as the ACL Document for a given other Document.
 *
 * @ignore Experimental API.
 * @param document [[VirtualDocument]] describing the Document this is the ACL Document for.
 */
function isAclFor(document) {
    return {
        internal_descriptor: {
            type: "IsAclFor",
            document: document
        }
    };
}
/**
 * Describe a Document that is referred to by a given Subject.
 *
 * @param subject [[VirtualSubject]] describing the Subject that points to this Document.
 * @param predicate Predicate that is used on `subject` to point to this Document.
 */
function isFoundOn$1(subject, predicate) {
    return {
        internal_descriptor: {
            type: "IsFoundOn",
            subject: subject,
            predicate: predicate
        }
    };
}
/**
 * Describe a Document that should be referred to by a given Subject.
 *
 * If the given Subject does not refer to a Document yet for the given Predicate, a new Document
 * will be created inside [[isEnsuredOn.fallbackContainer]] and added to the given Subject for the
 * given Predicate.
 *
 * @param subject [[VirtualSubject]] describing the Subject that points to this Document.
 * @param predicate Predicate that is used on `subject` to point to this Document.
 * @param fallbackContainer [[VirtualContainer]] describing the Container in which the Document
 *                          should be created if it does not exist yet.
 */
function isEnsuredOn(subject, predicate, fallbackContainer) {
    var descriptor = {
        type: "IsEnsuredOn",
        subject: subject,
        predicate: predicate,
        fallbackContainer: fallbackContainer,
        acl: {}
    };
    return generateEnsuredVirtualDocument(descriptor);
}
/**
 * @ignore Internal API.
 */
function generateEnsuredVirtualDocument(descriptor) {
    return {
        internal_descriptor: descriptor,
        experimental_isReadableByEveryone: generateSetPublicAcl$1(descriptor, "read"),
        experimental_isAppendableByEveryone: generateSetPublicAcl$1(descriptor, "append"),
        experimental_isWritableByEveryone: generateSetPublicAcl$1(descriptor, "write"),
        experimental_isControllableByEveryone: generateSetPublicAcl$1(descriptor, "control"),
        experimental_isReadableByAgent: generateSetAgentAcl$1(descriptor, "read"),
        experimental_isAppendableByAgent: generateSetAgentAcl$1(descriptor, "append"),
        experimental_isWritableByAgent: generateSetAgentAcl$1(descriptor, "write"),
        experimental_isControllableByAgent: generateSetAgentAcl$1(descriptor, "control"),
        experimental_isReadableByOrigin: generateSetOriginAcl$1(descriptor, "read"),
        experimental_isAppendableByOrigin: generateSetOriginAcl$1(descriptor, "append"),
        experimental_isWritableByOrigin: generateSetOriginAcl$1(descriptor, "write"),
        experimental_isControllableByOrigin: generateSetOriginAcl$1(descriptor, "control")
    };
}
/**
 * @ignore Internal API.
 */
function generateSetOriginAcl$1(descriptor, accessMode) {
    return function (origin, agent) {
        var _a, _b, _c, _d;
        var acl = (_a = descriptor.acl, (_a !== null && _a !== void 0 ? _a : {}));
        acl.origins = (_b = acl.origins, (_b !== null && _b !== void 0 ? _b : {}));
        acl.origins[origin] = (_c = acl.origins[origin], (_c !== null && _c !== void 0 ? _c : {}));
        acl.origins[origin][agent] = (_d = acl.origins[origin][agent], (_d !== null && _d !== void 0 ? _d : {}));
        acl.origins[origin][agent][accessMode] = true;
        var newDescriptor = __assign(__assign({}, descriptor), { acl: acl });
        return generateEnsuredVirtualDocument(newDescriptor);
    };
}
/**
 * @ignore Internal API.
 */
function generateSetAgentAcl$1(descriptor, accessMode) {
    return function (agent) {
        var _a, _b, _c;
        var acl = (_a = descriptor.acl, (_a !== null && _a !== void 0 ? _a : {}));
        acl.agents = (_b = acl.agents, (_b !== null && _b !== void 0 ? _b : {}));
        acl.agents[agent] = (_c = acl.agents[agent], (_c !== null && _c !== void 0 ? _c : {}));
        acl.agents[agent][accessMode] = true;
        var newDescriptor = __assign(__assign({}, descriptor), { acl: acl });
        return generateEnsuredVirtualDocument(newDescriptor);
    };
}
/**
 * @ignore Internal API.
 */
function generateSetPublicAcl$1(descriptor, accessMode) {
    return function () {
        var _a, _b;
        var acl = (_a = descriptor.acl, (_a !== null && _a !== void 0 ? _a : {}));
        acl.public = (_b = acl.public, (_b !== null && _b !== void 0 ? _b : {}));
        acl.public[accessMode] = true;
        var newDescriptor = __assign(__assign({}, descriptor), { acl: acl });
        return generateEnsuredVirtualDocument(newDescriptor);
    };
}

/**
 * @ignore
 */
function internal_isIsFoundAt(virtualDocument) {
    return virtualDocument.internal_descriptor.type === "IsFoundAt";
}
/**
 * @ignore
 */
function internal_isIsAclFor(virtualDocument) {
    return virtualDocument.internal_descriptor.type === "IsAclFor";
}
/**
 * @ignore
 */
function internal_isIsFoundOn(virtualDocument) {
    return virtualDocument.internal_descriptor.type === "IsFoundOn";
}
/**
 * @ignore
 */
function internal_isIsEnsuredOn(virtualDocument) {
    return virtualDocument.internal_descriptor.type === "IsEnsuredOn";
}

function internal_isIsFoundAt$1(virtualSubject) {
    return virtualSubject.internal_descriptor.type === "IsFoundAt";
}
function internal_isIsFoundOn$1(virtualSubject) {
    return virtualSubject.internal_descriptor.type === "IsFoundOn";
}
function internal_isIsEnsuredOn$1(virtualSubject) {
    return virtualSubject.internal_descriptor.type === "IsEnsuredOn";
}
function internal_isWithRefLocator(locator) {
    return Array.isArray(locator.references);
}
function internal_isAsRefLocator(locator) {
    return typeof locator.reference === "string";
}
function internal_isIsFoundIn(virtualSubject) {
    return virtualSubject.internal_descriptor.type === "IsFoundIn";
}
function internal_isIsEnsuredIn(virtualSubject) {
    return virtualSubject.internal_descriptor.type === "IsEnsuredIn";
}

/**
 * Fetch the given Subject.
 *
 * Performs the HTTP requests needed to fetch the Subject described by `virtualSubject`.
 *
 * @ignore Experimental API.
 * @param virtualSubject [[VirtualSubject]] that is to be fetched.
 */
function internal_fetchSubject(virtualSubject) {
    return __awaiter(this, void 0, void 0, function () {
        var promise;
        return __generator(this, function (_a) {
            if (typeof virtualSubject.promise !== "undefined") {
                return [2 /*return*/, virtualSubject.promise];
            }
            promise = internal_isIsFoundAt$1(virtualSubject)
                ? fetchByRef(virtualSubject)
                : internal_isIsFoundOn$1(virtualSubject)
                    ? getOnSubject(virtualSubject)
                    : internal_isIsEnsuredOn$1(virtualSubject)
                        ? ensureOnSubject(virtualSubject)
                        : internal_isIsFoundIn(virtualSubject)
                            ? getWithRefs(virtualSubject)
                            : internal_isIsEnsuredIn(virtualSubject)
                                ? ensureWithRefs(virtualSubject)
                                : Promise.reject(new Error("This type of Virtual Subject can not be processed yet."));
            virtualSubject.promise = promise;
            return [2 /*return*/, promise];
        });
    });
}
var fetchByRef = function (virtualSubject) { return __awaiter(void 0, void 0, void 0, function () {
    var document;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, fetchDocument$1(virtualSubject.internal_descriptor.reference)];
            case 1:
                document = _a.sent();
                return [2 /*return*/, document.getSubject(virtualSubject.internal_descriptor.reference)];
        }
    });
}); };
/**
 * @ignore Internal API.
 */
var getOnSubject = function (virtualSubject) { return __awaiter(void 0, void 0, void 0, function () {
    var sourceSubject, subjectRef, parentDocument;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, internal_fetchSubject(virtualSubject.internal_descriptor.subject)];
            case 1:
                sourceSubject = _a.sent();
                if (sourceSubject === null) {
                    return [2 /*return*/, null];
                }
                subjectRef = sourceSubject.getRef(virtualSubject.internal_descriptor.predicate);
                if (subjectRef === null) {
                    return [2 /*return*/, null];
                }
                parentDocument = sourceSubject.getDocument();
                /* istanbul ignore if [This should always be true, given that we just fetched it.] */
                if (!isSavedToPod(parentDocument)) {
                    return [2 /*return*/, null];
                }
                return [2 /*return*/, parentDocument.getSubject(subjectRef)];
        }
    });
}); };
/**
 * @ignore Internal API.
 */
var ensureOnSubject = function (virtualSubject) { return __awaiter(void 0, void 0, void 0, function () {
    var sourceSubject, subjectRef, parentDocument, sourceDoc, newSubject, updatedDoc;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, internal_fetchSubject(virtualSubject.internal_descriptor.subject)];
            case 1:
                sourceSubject = _a.sent();
                if (sourceSubject === null) {
                    return [2 /*return*/, null];
                }
                subjectRef = sourceSubject.getRef(virtualSubject.internal_descriptor.predicate);
                parentDocument = sourceSubject.getDocument();
                if (typeof subjectRef === "string" && isSavedToPod(parentDocument)) {
                    return [2 /*return*/, parentDocument.getSubject(subjectRef)];
                }
                sourceDoc = sourceSubject.getDocument();
                /* istanbul ignore if [This should always be true, given that we just fetched it.] */
                if (!hasRef(sourceDoc)) {
                    return [2 /*return*/, null];
                }
                newSubject = sourceDoc.addSubject();
                sourceSubject.addRef(virtualSubject.internal_descriptor.predicate, newSubject.asRef());
                return [4 /*yield*/, sourceDoc.save([sourceSubject, newSubject])];
            case 2:
                updatedDoc = _a.sent();
                return [2 /*return*/, updatedDoc.getSubject(newSubject.asRef())];
        }
    });
}); };
/**
 * @ignore Internal API.
 */
var getWithRefs = function (virtualSubject) { return __awaiter(void 0, void 0, void 0, function () {
    var document, locator;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, fetchDocument(virtualSubject.internal_descriptor.document)];
            case 1:
                document = _a.sent();
                if (document === null) {
                    return [2 /*return*/, null];
                }
                locator = virtualSubject.internal_descriptor.locator;
                if (internal_isWithRefLocator(locator)) {
                    return [2 /*return*/, getWithRefsFromDoc(document, locator)];
                }
                else if (internal_isAsRefLocator(locator)) {
                    return [2 /*return*/, getAsRefFromDoc(document, locator)];
                }
                throw new Error("This type of Locator can not be processed yet.");
        }
    });
}); };
/**
 * @ignore Internal API.
 */
function getWithRefsFromDoc(document, locator) {
    var references = locator.references;
    if (references.length === 0) {
        // TODO: Support just fetching one of the Subjects in this Document, if any
        //       (This requires an update on Tripledoc.)
        throw new Error("Please specify at least one property to identify this subject with.");
    }
    var subjectsMatchingFirstRef = document.findSubjects(references[0].predicate, references[0].object);
    var matchingSubjects = subjectsMatchingFirstRef.filter(function (subject) {
        return references.every(function (_a) {
            var predicate = _a.predicate, object = _a.object;
            return subject.getRef(predicate) === object;
        });
    });
    if (matchingSubjects.length !== 1) {
        return null;
    }
    return matchingSubjects[0];
}
/**
 * @ignore Internal API.
 */
function getAsRefFromDoc(document, locator) {
    return document.getSubject(locator.reference);
}
/**
 * @ignore Internal API.
 */
var ensureWithRefs = function (virtualSubject) { return __awaiter(void 0, void 0, void 0, function () {
    var document, locator;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, fetchDocument(virtualSubject.internal_descriptor.document)];
            case 1:
                document = _a.sent();
                if (document === null) {
                    return [2 /*return*/, null];
                }
                locator = virtualSubject.internal_descriptor.locator;
                if (internal_isWithRefLocator(locator)) {
                    return [2 /*return*/, ensureWithRefsInDoc(document, locator)];
                }
                else if (internal_isAsRefLocator(locator)) {
                    return [2 /*return*/, ensureAsRefInDoc(document, locator)];
                }
                throw new Error("This type of Locator can not be processed yet.");
        }
    });
}); };
/**
 * @ignore Internal API.
 */
function ensureWithRefsInDoc(document, locator) {
    return __awaiter(this, void 0, void 0, function () {
        var references, subjectsMatchingFirstRef, matchingSubjects, newSubject, rootSubject, updatedDocument;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    references = locator.references;
                    if (references.length === 0) {
                        // TODO: Support just fetching one of the Subjects in this Document, if any
                        //       (This requires an update on Tripledoc.)
                        throw new Error("Please specify at least one property to identify this subject with.");
                    }
                    subjectsMatchingFirstRef = document.findSubjects(references[0].predicate, references[0].object);
                    matchingSubjects = subjectsMatchingFirstRef.filter(function (subject) {
                        return references.every(function (_a) {
                            var predicate = _a.predicate, object = _a.object;
                            return subject.getRef(predicate) === object;
                        });
                    });
                    if (matchingSubjects.length > 0) {
                        return [2 /*return*/, matchingSubjects[0]];
                    }
                    newSubject = document.addSubject();
                    references.forEach(function (reference) {
                        return newSubject.addRef(reference.predicate, reference.object);
                    });
                    rootSubject = document.getSubject(document.asRef());
                    rootSubject.addRef("http://purl.org/dc/terms/references", newSubject.asRef());
                    return [4 /*yield*/, document.save([newSubject, rootSubject])];
                case 1:
                    updatedDocument = _a.sent();
                    return [2 /*return*/, updatedDocument.getSubject(newSubject.asRef())];
            }
        });
    });
}
/**
 * @ignore Internal API.
 */
function ensureAsRefInDoc(document, locator) {
    var subject = document.getSubject(locator.reference);
    if (subject !== null) {
        return subject;
    }
    // Usually, the reference will be a full URL, from which we only need the hash
    // (but without the leading `#`).
    // In case it's just the identifier, we first convert it to a full URL
    // relative to the Document, and then take the hash of that.
    var subjectRef = new URL(locator.reference, document.asRef()).hash.substring(1);
    var newSubject = document.addSubject({ identifier: subjectRef });
    return newSubject;
}

/**
 * @ignore
 */
function internal_isIsFoundAt$2(virtualContainer) {
    return virtualContainer.internal_descriptor.type === "IsFoundAt";
}
/**
 * @ignore
 */
function internal_isIsFoundOn$2(virtualContainer) {
    return virtualContainer.internal_descriptor.type === "IsFoundOn";
}
/**
 * @ignore
 */
function internal_isIsContainedIn(virtualContainer) {
    return virtualContainer.internal_descriptor.type === "IsContainedIn";
}

// Common vocab IRI's inlined to avoid inadvertently bundling a full copy of rdf-namespaces
// in projects that do not have a bundler/tree-shaking set up.
var acl = {
    Authorization: "http://www.w3.org/ns/auth/acl#Authorization",
    Read: "http://www.w3.org/ns/auth/acl#Read",
    Append: "http://www.w3.org/ns/auth/acl#Append",
    Write: "http://www.w3.org/ns/auth/acl#Write",
    Control: "http://www.w3.org/ns/auth/acl#Control",
    accessTo: "http://www.w3.org/ns/auth/acl#accessTo",
    agent: "http://www.w3.org/ns/auth/acl#agent",
    agentClass: "http://www.w3.org/ns/auth/acl#agentClass",
    default__workaround: "http://www.w3.org/ns/auth/acl#default",
    defaultForNew: "http://www.w3.org/ns/auth/acl#defaultForNew",
    mode: "http://www.w3.org/ns/auth/acl#mode",
    origin: "http://www.w3.org/ns/auth/acl#origin"
};
var foaf = { Agent: "http://xmlns.com/foaf/0.1/Agent" };
var rdf = { type: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type" };
/**
 * @ignore Internal API.
 */
function hasAclSettings(aclSettings) {
    return (aclSettings.public !== undefined ||
        aclSettings.origins !== undefined ||
        aclSettings.agents !== undefined);
}
/**
 * @ignore Internal API.
 */
function configureAcl(documentRef, aclRef, aclSettings, options) {
    if (options === void 0) { options = {}; }
    return __awaiter(this, void 0, void 0, function () {
        var aclDoc, e_1, publicAclSettings, authSubject, potentialSubjects, agentAclSettings, originAclSettings, savedAclDoc;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!hasAclSettings(aclSettings)) {
                        return [2 /*return*/, null];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fetchDocument$1(aclRef)];
                case 2:
                    aclDoc = _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    e_1 = _a.sent();
                    aclDoc = createDocument(aclRef);
                    return [3 /*break*/, 4];
                case 4:
                    if (aclDoc === undefined) {
                        throw new Error("Could not fetch the Access Control List of this Document.");
                    }
                    publicAclSettings = aclSettings.public;
                    if (publicAclSettings !== undefined) {
                        authSubject = void 0;
                        if (isSavedToPod(aclDoc)) {
                            potentialSubjects = aclDoc
                                .findSubjects(acl.accessTo, documentRef)
                                .filter(function (potentialSubject) {
                                return (potentialSubject.getRef(rdf.type) === acl.Authorization &&
                                    potentialSubject.getRef(acl.agentClass) === foaf.Agent);
                            });
                            authSubject =
                                potentialSubjects.length === 1
                                    ? potentialSubjects[0]
                                    : aclDoc.addSubject();
                        }
                        else {
                            authSubject = aclDoc.addSubject();
                        }
                        authSubject.setRef(rdf.type, acl.Authorization);
                        authSubject.setRef(acl.accessTo, documentRef);
                        authSubject.setRef(acl.agentClass, foaf.Agent);
                        if (options.default) {
                            authSubject.addRef(acl.default__workaround, documentRef);
                            // Deprecated, but included for backwards compatibility:
                            authSubject.addRef(acl.defaultForNew, documentRef);
                        }
                        if (publicAclSettings.read === true) {
                            authSubject.addRef(acl.mode, acl.Read);
                        }
                        else if (publicAclSettings.read === false) {
                            authSubject.removeRef(acl.mode, acl.Read);
                        }
                        if (publicAclSettings.append === true) {
                            authSubject.addRef(acl.mode, acl.Append);
                        }
                        else if (publicAclSettings.append === false) {
                            authSubject.removeRef(acl.mode, acl.Append);
                        }
                        if (publicAclSettings.write === true) {
                            authSubject.addRef(acl.mode, acl.Write);
                        }
                        else if (publicAclSettings.write === false) {
                            authSubject.removeRef(acl.mode, acl.Write);
                        }
                        if (publicAclSettings.control === true) {
                            authSubject.addRef(acl.mode, acl.Control);
                        }
                        else if (publicAclSettings.control === false) {
                            authSubject.removeRef(acl.mode, acl.Control);
                        }
                    }
                    agentAclSettings = aclSettings.agents;
                    if (agentAclSettings !== undefined) {
                        Object.keys(agentAclSettings).forEach(function (agent) {
                            var authSubject;
                            if (isSavedToPod(aclDoc)) {
                                var potentialSubjects = aclDoc
                                    .findSubjects(acl.accessTo, documentRef)
                                    .filter(function (potentialSubject) {
                                    return (potentialSubject.getRef(rdf.type) === acl.Authorization &&
                                        potentialSubject.getRef(acl.agent) === agent &&
                                        // Make sure that this Subject is not restricted to a specific Origin
                                        potentialSubject.getRef(acl.origin) === null);
                                });
                                authSubject =
                                    potentialSubjects.length === 1
                                        ? potentialSubjects[0]
                                        : aclDoc.addSubject();
                            }
                            else {
                                authSubject = aclDoc.addSubject();
                            }
                            authSubject.setRef(rdf.type, acl.Authorization);
                            authSubject.setRef(acl.accessTo, documentRef);
                            authSubject.setRef(acl.agent, agent);
                            if (options.default) {
                                authSubject.addRef(acl.default__workaround, documentRef);
                                // Deprecated, but included for backwards compatibility:
                                authSubject.addRef(acl.defaultForNew, documentRef);
                            }
                            if (agentAclSettings[agent].read === true) {
                                authSubject.addRef(acl.mode, acl.Read);
                            }
                            else if (agentAclSettings[agent].read === false) {
                                authSubject.removeRef(acl.mode, acl.Read);
                            }
                            if (agentAclSettings[agent].append === true) {
                                authSubject.addRef(acl.mode, acl.Append);
                            }
                            else if (agentAclSettings[agent].append === false) {
                                authSubject.removeRef(acl.mode, acl.Append);
                            }
                            if (agentAclSettings[agent].write === true) {
                                authSubject.addRef(acl.mode, acl.Write);
                            }
                            else if (agentAclSettings[agent].write === false) {
                                authSubject.removeRef(acl.mode, acl.Write);
                            }
                            if (agentAclSettings[agent].control === true) {
                                authSubject.addRef(acl.mode, acl.Control);
                            }
                            else if (agentAclSettings[agent].control === false) {
                                authSubject.removeRef(acl.mode, acl.Control);
                            }
                        });
                    }
                    originAclSettings = aclSettings.origins;
                    if (originAclSettings !== undefined) {
                        Object.keys(originAclSettings).forEach(function (origin) {
                            Object.keys(originAclSettings[origin]).forEach(function (agent) {
                                var authSubject;
                                if (isSavedToPod(aclDoc)) {
                                    var potentialSubjects = aclDoc
                                        .findSubjects(acl.accessTo, documentRef)
                                        .filter(function (potentialSubject) {
                                        return (potentialSubject.getRef(rdf.type) === acl.Authorization &&
                                            potentialSubject.getRef(acl.agent) === agent &&
                                            potentialSubject.getRef(acl.origin) === origin);
                                    });
                                    authSubject =
                                        potentialSubjects.length === 1
                                            ? potentialSubjects[0]
                                            : aclDoc.addSubject();
                                }
                                else {
                                    authSubject = aclDoc.addSubject();
                                }
                                authSubject.setRef(rdf.type, acl.Authorization);
                                authSubject.setRef(acl.accessTo, documentRef);
                                authSubject.setRef(acl.origin, origin);
                                authSubject.setRef(acl.agent, agent);
                                if (options.default) {
                                    authSubject.addRef(acl.default__workaround, documentRef);
                                    // Deprecated, but included for backwards compatibility:
                                    authSubject.addRef(acl.defaultForNew, documentRef);
                                }
                                if (originAclSettings[origin][agent].read === true) {
                                    authSubject.addRef(acl.mode, acl.Read);
                                }
                                else if (originAclSettings[origin][agent].read === false) {
                                    authSubject.removeRef(acl.mode, acl.Read);
                                }
                                if (originAclSettings[origin][agent].append === true) {
                                    authSubject.addRef(acl.mode, acl.Append);
                                }
                                else if (originAclSettings[origin][agent].append === false) {
                                    authSubject.removeRef(acl.mode, acl.Append);
                                }
                                if (originAclSettings[origin][agent].write === true) {
                                    authSubject.addRef(acl.mode, acl.Write);
                                }
                                else if (originAclSettings[origin][agent].write === false) {
                                    authSubject.removeRef(acl.mode, acl.Write);
                                }
                                if (originAclSettings[origin][agent].control === true) {
                                    authSubject.addRef(acl.mode, acl.Control);
                                }
                                else if (originAclSettings[origin][agent].control === false) {
                                    authSubject.removeRef(acl.mode, acl.Control);
                                }
                            });
                        });
                    }
                    return [4 /*yield*/, aclDoc.save()];
                case 5:
                    savedAclDoc = _a.sent();
                    return [2 /*return*/, savedAclDoc];
            }
        });
    });
}

/**
 * Fetch the given Container.
 *
 * Performs the HTTP requests needed to fetch the Container described by `virtualContainer`.
 *
 * @ignore Experimental API.
 * @param virtualContainer [[VirtualContainer]] that is to be fetched.
 */
function internal_fetchContainer(virtualContainer
// TODO: Add a TripleContainer to Tripledoc and return it here:
) {
    return __awaiter(this, void 0, void 0, function () {
        var promise;
        return __generator(this, function (_a) {
            if (typeof virtualContainer.promise !== "undefined") {
                return [2 /*return*/, virtualContainer.promise];
            }
            promise = internal_isIsFoundAt$2(virtualContainer)
                ? fetchByRef$1(virtualContainer)
                : internal_isIsFoundOn$2(virtualContainer)
                    ? getForRef(virtualContainer)
                    : internal_isIsContainedIn(virtualContainer)
                        ? ensureInContainer(virtualContainer)
                        : Promise.reject(new Error("This type of Virtual Container can not be processed yet."));
            virtualContainer.promise = promise;
            return [2 /*return*/, promise];
        });
    });
}
var fetchByRef$1 = function (virtualContainer) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, virtualContainer.internal_descriptor.reference];
    });
}); };
var getForRef = function (virtualContainer) { return __awaiter(void 0, void 0, void 0, function () {
    var subject, reference;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, internal_fetchSubject(virtualContainer.internal_descriptor.subject)];
            case 1:
                subject = _a.sent();
                if (subject === null) {
                    return [2 /*return*/, null];
                }
                reference = subject.getRef(virtualContainer.internal_descriptor.predicate);
                return [2 /*return*/, reference];
        }
    });
}); };
/**
 * @ignore Internal API.
 */
var ensureInContainer = function (virtualContainer) { return __awaiter(void 0, void 0, void 0, function () {
    var parentContainer, containerRef, e_1, dummyFileRef, containerDocument, aclRef;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, internal_fetchContainer(virtualContainer.internal_descriptor.container)];
            case 1:
                parentContainer = _a.sent();
                if (parentContainer === null) {
                    return [2 /*return*/, null];
                }
                containerRef = 
                // Remove trailing slashes from the parent Container:
                parentContainer.replace(/\/$/, "") +
                    "/" +
                    // Remove leading and trailing slashes from the name:
                    virtualContainer.internal_descriptor.name
                        .replace(/^\//, "")
                        .replace(/\/$/, "") +
                    "/";
                _a.label = 2;
            case 2:
                _a.trys.push([2, 4, , 5]);
                return [4 /*yield*/, fetchDocument$1(containerRef)];
            case 3:
                _a.sent();
                // No error thrown, so the Container exists:
                return [2 /*return*/, containerRef];
            case 4:
                e_1 = _a.sent();
                return [3 /*break*/, 5];
            case 5:
                dummyFileRef = containerRef + ".dummy";
                return [4 /*yield*/, createDocument(dummyFileRef).save()];
            case 6:
                _a.sent();
                // TODO Consider adding deletion to Tripledoc?
                return [4 /*yield*/, SolidAuth.fetch(dummyFileRef, { method: "DELETE" })];
            case 7:
                // TODO Consider adding deletion to Tripledoc?
                _a.sent();
                return [4 /*yield*/, fetchDocument$1(containerRef)];
            case 8:
                containerDocument = _a.sent();
                if (!hasAclSettings(virtualContainer.internal_descriptor.acl)) return [3 /*break*/, 10];
                aclRef = containerDocument.getAclRef();
                if (aclRef === null) {
                    throw new Error("Could not find a location for the Access Control List of this Container.");
                }
                return [4 /*yield*/, configureAcl(containerRef, aclRef, virtualContainer.internal_descriptor.acl, { default: true })];
            case 9:
                _a.sent();
                _a.label = 10;
            case 10: return [2 /*return*/, containerRef];
        }
    });
}); };

/**
 * Fetch the given Document.
 *
 * Performs the HTTP requests needed to fetch the Document described by `virtualDoc`.
 *
 * @param virtualDoc A [[VirtualDocument]] describing the Document you want to fetch
 * @returns A TripleDocument representing the contents of `virtualDoc` on the server.
 */
function fetchDocument(virtualDoc) {
    return __awaiter(this, void 0, void 0, function () {
        var promise, wrappedPromise;
        return __generator(this, function (_a) {
            if (typeof virtualDoc.promise !== "undefined") {
                return [2 /*return*/, virtualDoc.promise];
            }
            promise = internal_isIsFoundAt(virtualDoc)
                ? fetchByRef$2(virtualDoc)
                : internal_isIsAclFor(virtualDoc)
                    ? getFromAcl(virtualDoc)
                    : internal_isIsFoundOn(virtualDoc)
                        ? getForRef$1(virtualDoc)
                        : internal_isIsEnsuredOn(virtualDoc)
                            ? ensureForRef(virtualDoc)
                            : Promise.reject(new Error("This type of Virtual Document can not be processed yet."));
            wrappedPromise = promise.then(function (fetchedDocument) {
                if (fetchedDocument !== null) {
                    // Update the cached Promise to return the updated Document when accessed again:
                    var wrappedSave = function (subjects) {
                        var savePromise = fetchedDocument.save(subjects);
                        virtualDoc.promise = savePromise;
                        return savePromise;
                    };
                    return __assign(__assign({}, fetchedDocument), { save: wrappedSave });
                }
                return null;
            });
            virtualDoc.promise = wrappedPromise;
            return [2 /*return*/, wrappedPromise];
        });
    });
}
/**
 * @ignore Internal API.
 */
var fetchByRef$2 = function (virtualDoc) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        return [2 /*return*/, fetchDocument$1(virtualDoc.internal_descriptor.reference)];
    });
}); };
/**
 * @ignore Internal API.
 */
var getFromAcl = function (virtualDoc) { return __awaiter(void 0, void 0, void 0, function () {
    var mainDocument, aclRef;
    var _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0: return [4 /*yield*/, fetchDocument(virtualDoc.internal_descriptor.document)];
            case 1:
                mainDocument = _b.sent();
                aclRef = (_a = mainDocument) === null || _a === void 0 ? void 0 : _a.getAclRef();
                if (!aclRef) {
                    return [2 /*return*/, null];
                }
                return [4 /*yield*/, fetchDocument$1(aclRef)];
            case 2: return [2 /*return*/, _b.sent()];
        }
    });
}); };
/**
 * @ignore Internal API.
 */
var getForRef$1 = function (virtualDoc) { return __awaiter(void 0, void 0, void 0, function () {
    var subject, reference;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, internal_fetchSubject(virtualDoc.internal_descriptor.subject)];
            case 1:
                subject = _a.sent();
                if (subject === null) {
                    return [2 /*return*/, null];
                }
                reference = subject.getRef(virtualDoc.internal_descriptor.predicate);
                if (reference === null) {
                    return [2 /*return*/, null];
                }
                return [2 /*return*/, fetchDocument$1(reference)];
        }
    });
}); };
/**
 * @ignore Internal API.
 */
var ensureForRef = function (virtualDoc) { return __awaiter(void 0, void 0, void 0, function () {
    var subject, reference, container, newDocument, savedDocument, subjectDoc, aclRef;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, internal_fetchSubject(virtualDoc.internal_descriptor.subject)];
            case 1:
                subject = _a.sent();
                if (subject === null) {
                    return [2 /*return*/, null];
                }
                reference = subject.getRef(virtualDoc.internal_descriptor.predicate);
                if (reference !== null) {
                    return [2 /*return*/, fetchDocument$1(reference)];
                }
                return [4 /*yield*/, internal_fetchContainer(virtualDoc.internal_descriptor.fallbackContainer)];
            case 2:
                container = _a.sent();
                if (!container) {
                    return [2 /*return*/, null];
                }
                newDocument = createDocumentInContainer(container);
                return [4 /*yield*/, newDocument.save()];
            case 3:
                savedDocument = _a.sent();
                subject.setRef(virtualDoc.internal_descriptor.predicate, savedDocument.asRef());
                subjectDoc = subject.getDocument();
                /* istanbul ignore if [This should always be true, given that we just fetched it.] */
                if (!hasRef(subjectDoc)) {
                    return [2 /*return*/, null];
                }
                return [4 /*yield*/, subjectDoc.save([subject])];
            case 4:
                _a.sent();
                if (!hasAclSettings(virtualDoc.internal_descriptor.acl)) return [3 /*break*/, 6];
                aclRef = savedDocument.getAclRef();
                if (aclRef === null) {
                    throw new Error("Could not find a location for the Access Control List of this Document.");
                }
                return [4 /*yield*/, configureAcl(savedDocument.asRef(), aclRef, virtualDoc.internal_descriptor.acl)];
            case 5:
                _a.sent();
                _a.label = 6;
            case 6: return [2 /*return*/, savedDocument];
        }
    });
}); };

/**
 * Construct a [[VirtualSubject]].
 *
 * This function allows you to programmatically construct a [[VirtualSubject]] by chaining
 * together a number of functions.
 */
function describeSubject() {
    return {
        isFoundAt: isFoundAt$2,
        isFoundOn: isFoundOn$2,
        isEnsuredOn: isEnsuredOn$1,
        isFoundIn: isFoundIn,
        isEnsuredIn: isEnsuredIn
    };
}
/**
 * Describe a Subject for which you know the IRI.
 *
 * @param reference IRI of the desired Subject.
 */
function isFoundAt$2(reference) {
    var descriptor = {
        reference: reference,
        type: "IsFoundAt"
    };
    return {
        internal_descriptor: descriptor
    };
}
/**
 * Describe a Subject that is referred to by a given other Subject.
 *
 * @param subject [[VirtualSubject]] describing the Subject that points to this Subject.
 * @param predicate Predicate that is used on `subject` to point to this Subject.
 */
function isFoundOn$2(subject, predicate) {
    var descriptor = {
        predicate: predicate,
        subject: subject,
        type: "IsFoundOn"
    };
    return {
        internal_descriptor: descriptor
    };
}
/**
 * Describe a Subject that should be referred to by a given other Subject.
 *
 * If the given Subject does not refer to another Subject yet for the given Predicate, a new Subject
 * will be created inside that Subject's containing Document and added to the given Subject for the
 * given Predicate.
 *
 * @param subject [[VirtualSubject]] describing the Subject that points to this Subject.
 * @param predicate Predicate that is used on `subject` to point to this Subject.
 */
function isEnsuredOn$1(subject, predicate) {
    var descriptor = {
        predicate: predicate,
        subject: subject,
        type: "IsEnsuredOn"
    };
    return {
        internal_descriptor: descriptor
    };
}
/**
 * @ignore Internal API.
 */
function generateIsFoundIn_WithRef_VirtualSubject(descriptor) {
    return function (predicate, object) {
        var _a;
        var locator = (_a = descriptor
            .locator, (_a !== null && _a !== void 0 ? _a : { references: [] }));
        var newReferences = locator.references.concat({
            predicate: predicate,
            object: object
        });
        var newDescriptor = __assign(__assign({}, descriptor), { locator: { references: newReferences } });
        var subjectWithRefAdder = {
            internal_descriptor: newDescriptor,
            withRef: generateIsFoundIn_WithRef_VirtualSubject(newDescriptor)
        };
        return subjectWithRefAdder;
    };
}
/**
 * @ignore Internal API.
 */
function generateIsFoundIn_AsRef_VirtualSubject(bareDescriptor) {
    return function (reference) {
        var descriptor = __assign(__assign({}, bareDescriptor), { locator: {
                reference: reference
            } });
        return {
            internal_descriptor: descriptor
        };
    };
}
/**
 * Describe a Subject that is contained in a given Document.
 *
 * @param document [[VirtualDocument]] describing the Document that contains to this Subject.
 */
function isFoundIn(document) {
    var bareDescriptor = {
        type: "IsFoundIn",
        document: document
    };
    return {
        internal_descriptor: bareDescriptor,
        withRef: generateIsFoundIn_WithRef_VirtualSubject(bareDescriptor),
        asRef: generateIsFoundIn_AsRef_VirtualSubject(bareDescriptor)
    };
}
/**
 * @ignore Internal API.
 */
function generateIsEnsuredIn_WithRef_VirtualSubject(descriptor) {
    return function (predicate, object) {
        var _a;
        var locator = (_a = descriptor
            .locator, (_a !== null && _a !== void 0 ? _a : { references: [] }));
        var newReferences = locator.references.concat({
            predicate: predicate,
            object: object
        });
        var newDescriptor = __assign(__assign({}, descriptor), { locator: { references: newReferences } });
        var subjectWithRefAdder = {
            internal_descriptor: newDescriptor,
            withRef: generateIsEnsuredIn_WithRef_VirtualSubject(newDescriptor)
        };
        return subjectWithRefAdder;
    };
}
/**
 * @ignore Internal API.
 */
function generateIsEnsuredIn_AsRef_VirtualSubject(bareDescriptor) {
    return function (reference) {
        var descriptor = __assign(__assign({}, bareDescriptor), { locator: {
                reference: reference
            } });
        return {
            internal_descriptor: descriptor
        };
    };
}
/**
 * Describe a Subject that should be contained in a given Document.
 *
 * @param document [[VirtualDocument]] describing the Document that should contain this Subject.
 */
function isEnsuredIn(document) {
    var bareDescriptor = {
        type: "IsEnsuredIn",
        document: document
    };
    return {
        internal_descriptor: bareDescriptor,
        withRef: generateIsEnsuredIn_WithRef_VirtualSubject(bareDescriptor),
        asRef: generateIsEnsuredIn_AsRef_VirtualSubject(bareDescriptor)
    };
}

function serialise(virtualDoc) {
    var descriptor = virtualDoc.internal_descriptor;
    var keys = Object.keys(descriptor);
    var serialised = keys.reduce(function (soFar, key) {
        var _a;
        var value = descriptor[key];
        var serialisedValue = hasDescriptor(value) ? serialise(value) : value;
        return __assign(__assign({}, soFar), (_a = {}, _a[key] = serialisedValue, _a));
    }, {});
    return serialised;
}
function hasDescriptor(value) {
    return (typeof value === "object" &&
        typeof value.internal_descriptor !== "undefined");
}

export { describeContainer, describeDocument, describeSubject, serialise as experimental_serialise, fetchDocument };
